import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShoppingBag, List, X } from "@phosphor-icons/react";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import CartSheet from "./CartSheet";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_vandys-gallery/artifacts/accmjott_vandy%27s%20logo.PNG";

export default function Header() {
  const { totalItems } = useCart();
  const { currency } = useCurrency();
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const CURRENCY_LABEL = { usd: "$", inr: "₹", aed: "AED" }[currency] || "$";

  const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/shop", label: "Shop" },
    { to: "/portfolio", label: "Portfolio" },
    { to: "/mail-club", label: "Mail Club" },
    { to: "/about", label: "About" },
  ];

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-50 bg-[#F5E6A0]/90 backdrop-blur-md border-b border-[#E01A23]/10"
      >
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 flex items-center justify-between h-14 md:h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src={LOGO_URL} alt="Vandy's" className="h-10 md:h-14 w-auto object-contain" style={{ mixBlendMode: "multiply" }} />
          </Link>

          <nav className="hidden md:flex items-center gap-6 lg:gap-10">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`text-sm tracking-wider transition-colors duration-300 whitespace-nowrap ${
                  location.pathname === link.to
                    ? "text-[#E01A23] font-medium"
                    : "text-[#4a4a4a] hover:text-[#E01A23]"
                }`}
                style={{ fontFamily: "'Courier New', Courier, monospace" }}
              >
                {link.label.toLowerCase()}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 md:gap-4">
            <span
              data-testid="header-currency-badge"
              title={`Prices shown in ${CURRENCY_LABEL}`}
              className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-full border border-[#E01A23]/30 text-[10px] md:text-xs text-[#E01A23] tracking-wider whitespace-nowrap"
              style={{ fontFamily: "'Courier New', Courier, monospace" }}
            >
              {CURRENCY_LABEL}
            </span>
            <button
              data-testid="open-cart-button"
              onClick={() => setCartOpen(true)}
              className="relative p-2 text-[#1A1A1A] hover:text-[#E01A23] transition-colors"
            >
              <ShoppingBag size={22} weight="regular" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#E01A23] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-[#1A1A1A]"
            >
              {mobileOpen ? <X size={24} /> : <List size={24} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden bg-[#F5E6A0] border-t border-[#E01A23]/10 px-6 py-6">
            <nav className="flex flex-col gap-4">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  onClick={() => setMobileOpen(false)}
                  className={`text-lg ${
                    location.pathname === link.to
                      ? "text-[#E01A23]"
                      : "text-[#4a4a4a] hover:text-[#E01A23]"
                  }`}
                  style={{ fontFamily: "'Lazy Dog', cursive" }}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      <CartSheet open={cartOpen} onOpenChange={setCartOpen} />
    </>
  );
}
